import React, { createContext, useContext, useState } from 'react';
import { Box, LinearProgress, Typography, Chip, IconButton, Collapse } from '@mui/material';
import { Close as CloseIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';

const UploadProgressContext = createContext();

export const useUploadProgress = () => {
  const context = useContext(UploadProgressContext);
  if (!context) {
    throw new Error('useUploadProgress must be used within an UploadProgressProvider');
  }
  return context;
};

export const UploadProgressProvider = ({ children }) => {
  const [uploads, setUploads] = useState({});

  const addUpload = (id, fileName) => {
    setUploads(prev => ({
      ...prev,
      [id]: {
        id,
        fileName,
        progress: 0,
        status: 'pending', // pending, uploading, processing, success, error
        error: null
      }
    }));
  };

  const updateUploadProgress = (id, progress) => {
    setUploads(prev => {
      if (!prev[id] || prev[id].status === 'error' || prev[id].status === 'success' || prev[id].status === 'processing') return prev; // Don't update if already completed or processing
      return {
        ...prev,
        [id]: {
          ...prev[id],
          progress,
          status: progress < 100 ? 'uploading' : 'processing' // Change to processing when upload completes
        }
      };
    });
  };

  const setUploadProcessing = (id) => {
    setUploads(prev => {
      if (!prev[id]) return prev;
      return {
        ...prev,
        [id]: {
          ...prev[id],
          status: 'processing',
          progress: 100
        }
      };
    });
  };

  const setUploadError = (id, error) => {
    setUploads(prev => {
      if (!prev[id]) return prev;
      return {
        ...prev,
        [id]: {
          ...prev[id],
          status: 'error',
          progress: 100, // Set to 100% on error
          error
        }
      };
    });
    
    // Automatically remove the upload after 10 seconds (10000ms) when there's an error
    setTimeout(() => {
      setUploads(prev => {
        const newUploads = { ...prev };
        delete newUploads[id];
        return newUploads;
      });
    }, 10000);
  };

  const setUploadSuccess = (id) => {
    setUploads(prev => {
      if (!prev[id] || prev[id].status === 'error' || prev[id].status === 'success') return prev; // Don't update if already completed
      return {
        ...prev,
        [id]: {
          ...prev[id],
          status: 'success',
          progress: 100
        }
      };
    });
    
    // Automatically remove the upload after 5 seconds (5000ms) when successful
    setTimeout(() => {
      setUploads(prev => {
        const newUploads = { ...prev };
        delete newUploads[id];
        return newUploads;
      });
    }, 5000);
  };

  const removeUpload = (id) => {
    setUploads(prev => {
      const newUploads = { ...prev };
      delete newUploads[id];
      return newUploads;
    });
  };

  const clearCompletedUploads = () => {
    setUploads(prev => {
      const newUploads = {};
      Object.keys(prev).forEach(key => {
        if (prev[key].status !== 'success' && prev[key].status !== 'error') {
          newUploads[key] = prev[key];
        }
      });
      return newUploads;
    });
  };

  const value = {
    uploads,
    addUpload,
    updateUploadProgress,
    setUploadProcessing,
    setUploadError,
    setUploadSuccess,
    removeUpload,
    clearCompletedUploads
  };

  return (
    <UploadProgressContext.Provider value={value}>
      {children}
    </UploadProgressContext.Provider>
  );
};

// Separate component to be used within Router context
export const GlobalUploadProgress = ({ shouldShow = false }) => {
  const { uploads, removeUpload } = useUploadProgress();
  const [expanded, setExpanded] = useState(true);
  
  // Show all uploads (including completed ones) when expanded
  const allUploads = Object.values(uploads);

  // Only render if shouldShow is true and there are uploads
  if (!shouldShow || (allUploads.length === 0 && expanded)) {
    return null;
  }

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 80,
        right: 16,
        zIndex: 9999,
        width: 300,
        backgroundColor: 'background.paper',
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1.5,
          borderBottom: allUploads.length > 0 ? '1px solid' : 'none',
          borderColor: 'divider',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }}
        onClick={handleToggle}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Upload Status ({allUploads.length})
        </Typography>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      
      <Collapse in={expanded}>
        <Box sx={{ p: 2, maxHeight: '70vh', overflowY: 'auto' }}>
          {allUploads.map((upload) => (
            <Box key={upload.id} sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" noWrap sx={{ flex: 1, mr: 1 }}>
                  {upload.fileName}
                </Typography>
                <Chip
                  label={upload.status === 'pending' ? 'Pending' : 
                         upload.status === 'uploading' ? 'Uploading' : 
                         upload.status === 'processing' ? 'Processing' : 
                         upload.status === 'success' ? 'Success' : 'Error'}
                  size="small"
                  color={upload.status === 'success' ? 'success' : 
                         upload.status === 'error' ? 'error' : 
                         upload.status === 'processing' ? 'warning' : 
                         upload.status === 'uploading' ? 'info' : 'default'}
                  variant="outlined"
                />
                <IconButton 
                  size="small" 
                  onClick={() => removeUpload(upload.id)}
                  sx={{ ml: 1 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              {upload.status !== 'success' && (
                <LinearProgress 
                  variant={upload.status === 'uploading' ? 'determinate' : 'buffer'}
                  value={upload.progress} 
                  valueBuffer={100}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {upload.status === 'success' ? 'Completed' : 
                 upload.status === 'error' ? upload.error : 
                 upload.status === 'processing' ? 'Processing data...' : 
                 `${Math.round(upload.progress)}%`}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};